import React, { useMemo } from 'react';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useIntl, IntlShape } from 'react-intl';

import { TimeHelper } from '../../../../utils';
import { useAppSelector } from '../../../../store/hooks';
import { Employee } from '../../../../types';
import { SeriesOutcomeEnum, SeriesStatusEnum } from '../../../../store/series/types';
import { buildSeriesTimelineRows, SeriesTimelineRow } from '../../util/seriesTimeline';

import styles from './SeriesEditTimeline.module.scss';

type SeriesEditTimelineProps = {
  editingEventId: string | null;
  onRetry: () => void;
  onToggleEdit?: (eventId: string) => void;
  onRecordOutcome?: (eventId: string) => void;
  onUndo?: (eventId: string) => void;
  undoingEventId?: string | null;
  onBookFollowUp?: () => void;
  inlineEditContent?: React.ReactNode;
};

function rowLabel(row: SeriesTimelineRow, intl: IntlShape): string {
  if (row.isFirstConsultation) {
    return intl.formatMessage({ id: 'service.firstConsultation' });
  }
  const n = row.followUpSeq ?? '';
  return intl.formatMessage({ id: 'seriesbooking.appointment.followUp' }, { n }).trim();
}

function formatEmployee(employee: Employee | undefined): string {
  if (!employee) return '';
  const first = (employee.firstName || '').trim();
  const last = (employee.lastName || '').trim();
  if (first && last) return `${first.charAt(0)}. ${last}`;
  return last || first || employee.name || '';
}

type StatusBadgeProps = {
  isOpen: boolean;
  outcome: SeriesOutcomeEnum | undefined;
};

// Closed badge tints by series outcome (BOUGHT → dollarGreen, FAILED → red).
// Falls back to neutral grey when an outcome isn't recorded — shouldn't
// happen in normal flows, but keeps the badge sensible for legacy data.
function StatusBadge({ isOpen, outcome }: StatusBadgeProps) {
  const intl = useIntl();

  if (isOpen) {
    return (
      <span className={`${styles.statusPill} ${styles.statusOpen}`}>
        {intl.formatMessage({ id: 'seriesbooking.detail.badge.open' })}
      </span>
    );
  }

  let outcomeLabel = '';
  let outcomeClass = styles.statusClosed;
  if (outcome === SeriesOutcomeEnum.BOUGHT) {
    outcomeLabel = intl.formatMessage({ id: 'seriesbooking.outcome.bought.label' });
    outcomeClass = styles.statusClosedBought;
  } else if (outcome === SeriesOutcomeEnum.FAILED) {
    outcomeLabel = intl.formatMessage({ id: 'seriesbooking.outcome.failed.label' });
    outcomeClass = styles.statusClosedFailed;
  }

  return (
    <span className={`${styles.statusPill} ${outcomeClass}`}>
      {intl.formatMessage(
        { id: 'seriesbooking.detail.badge.closedWithOutcome' },
        { outcome: outcomeLabel },
      )}
    </span>
  );
}

type EventCardProps = {
  row: SeriesTimelineRow;
  employeeName: string;
  onEdit?: () => void;
  onRecordOutcome?: () => void;
  isEditing?: boolean;
  inlineEditContent?: React.ReactNode;
};

function EventCardComponent({ row, employeeName, onEdit, onRecordOutcome, isEditing, inlineEditContent }: EventCardProps) {
  const intl = useIntl();
  const label = rowLabel(row, intl);
  const timeLabel = TimeHelper.getTimeByMinutes(row.event.time);
  const endTimeLabel = TimeHelper.getTimeByMinutes(row.event.time + (row.event.duration || 0));

  return (
    <div
      className={styles.currentCard}
      data-testid={`series-edit-timeline-row-${row.event.id}`}
      data-variant={row.variant}
    >
      <div className={styles.currentHeader}>
        <span className={styles.currentLabel}>{label}</span>
      </div>
      <div className={styles.currentDate}>{row.event.date}</div>
      <div className={styles.currentTime}>{timeLabel} – {endTimeLabel}</div>
      {employeeName !== '' && (
        <div className={styles.currentEmployee}>{employeeName}</div>
      )}
      <div className={styles.currentActions}>
        {onEdit && (
          <Button
            variant="outlined"
            size="small"
            onClick={onEdit}
            data-testid={`series-edit-event-${row.event.id}`}
            className={styles.actionBtn}
          >
            {isEditing
              ? intl.formatMessage({ id: 'common.abort' })
              : intl.formatMessage({ id: 'seriesbooking.common.change' })}
          </Button>
        )}
        {onRecordOutcome && (
          <Button
            variant="contained"
            size="small"
            onClick={onRecordOutcome}
            data-testid={`series-record-outcome-${row.event.id}`}
            className={styles.recordOutcomeBtn}
          >
            {intl.formatMessage({ id: 'seriesbooking.outcome.recordOutcome' })}
          </Button>
        )}
      </div>
      {isEditing && inlineEditContent && (
        <div className={styles.inlineEdit}>{inlineEditContent}</div>
      )}
    </div>
  );
}
const EventCard = React.memo(EventCardComponent);

type LockedRowProps = {
  row: SeriesTimelineRow;
  employeeName: string;
  onUndo?: () => void;
  isUndoing?: boolean;
};

function LockedRowComponent({ row, employeeName, onUndo, isUndoing }: LockedRowProps) {
  const intl = useIntl();
  const label = rowLabel(row, intl);

  let dotClassName = `${styles.dot} ${styles.dotCompleted}`;
  let DotIcon = CheckIcon;
  if (row.variant === 'cancelled') {
    dotClassName = `${styles.dot} ${styles.dotCancelled}`;
    DotIcon = ReplayIcon;
  }

  const undoLabel = intl.formatMessage({ id: 'seriesbooking.series.undo' });

  let undoIcon: React.ReactNode = <ReplayIcon />;
  if (isUndoing) {
    undoIcon = <CircularProgress size={25} />;
  }

  return (
    <div
      className={styles.completedRow}
      data-testid={`series-edit-timeline-row-${row.event.id}`}
      data-variant={row.variant}
    >
      <div className={dotClassName}>
        <DotIcon className={styles.dotIcon} />
      </div>
      <div className={styles.completedBody}>
        <span className={styles.completedLabel}>{label}</span>
        <span className={styles.completedDate}>{row.event.date}</span>
      </div>
      {employeeName !== '' && (
        <span className={styles.completedEmployee}>{employeeName}</span>
      )}

      <div className={styles.completedRight}>
        {row.variant === 'cancelled' && (
          <span className={styles.cancelledChip}>
            {intl.formatMessage({ id: 'seriesbooking.outcome.cancelled.label' })}
          </span>
        )}
        {onUndo && (
          <Tooltip title={undoLabel}>
            <span className={styles.undoBtnWrapper}>
              <IconButton
                size="small"
                onClick={onUndo}
                disabled={isUndoing}
                aria-label={undoLabel}
                data-testid={`series-undo-${row.event.id}`}
                className={styles.undoBtn}
              >
                {undoIcon}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

LockedRowComponent.defaultProps = {
  onUndo: undefined,
  isUndoing: false,
};

const LockedRow = React.memo(LockedRowComponent);

function SeriesEditTimelineComponent({
  editingEventId,
  onRetry,
  onToggleEdit,
  onRecordOutcome,
  onUndo,
  undoingEventId,
  onBookFollowUp,
  inlineEditContent,
}: SeriesEditTimelineProps) {
  const intl = useIntl();
  const seriesState = useAppSelector((state) => state.series.getSeriesById);
  const employees = useAppSelector((state) => state.employees.storeEmployees.data.employees);

  const employeesById = useMemo<Record<string, Employee>>(() => {
    const map: Record<string, Employee> = {};
    employees.forEach((e) => { map[e.id] = e; });
    return map;
  }, [employees]);

  const getEmployeeNameForEvent = (event: SeriesTimelineRow['event']): string => {
    const eventTypeData = (event.eventTypeData as any) || {};
    const employeeId: string | undefined = eventTypeData.suggestedEmployee;
    if (!employeeId) return '';
    return formatEmployee(employeesById[employeeId]);
  };

  if (seriesState.loading || seriesState.data === null) {
    return (
      <div className={styles.wrapper} data-testid="series-edit-timeline">
        <div className={styles.loading} data-testid="series-edit-timeline-loading">
          <CircularProgress size={20} />
        </div>
      </div>
    );
  }

  if (seriesState.error !== null) {
    return (
      <div className={styles.wrapper} data-testid="series-edit-timeline">
        <div className={styles.error} data-testid="series-edit-timeline-error">
          <span>{intl.formatMessage({ id: 'common.defaultError' })}</span>
          <button type="button" onClick={onRetry}>
            {intl.formatMessage({ id: 'common.retry' })}
          </button>
        </div>
      </div>
    );
  }

  const series = seriesState.data.series;
  const isOpen = series.status === SeriesStatusEnum.OPEN;

  const rows = buildSeriesTimelineRows({
    events: seriesState.data.events,
    rootEventId: series.rootEventId,
    nowMs: Date.now(),
  });

  const allFollowUps = rows.filter((r) => !r.isFirstConsultation && r.followUpSeq !== null);
  const lastFollowUpSeq = allFollowUps.reduce((max, r) => Math.max(max, r.followUpSeq || 0), 0);
  const nextFollowUpSeq = lastFollowUpSeq + 1;
  const nextFollowUpLabel = intl.formatMessage(
    { id: 'seriesbooking.appointment.followUp' },
    { n: nextFollowUpSeq },
  );

  const isFollowupNeeded = !rows.length || rows.every((r) => r.variant !== 'needsOutcome');

  return (
    <div className={styles.wrapper} data-testid="series-edit-timeline">
      <div className={styles.headerRow}>
        <span className={styles.headerTitle}>
          {intl.formatMessage({ id: 'seriesbooking.detail.haSeries' })}
        </span>
        <StatusBadge isOpen={isOpen} outcome={series.outcome} />
      </div>

      {rows.map((row) => {
        const isLocked = row.variant === 'cancelled' || row.variant === 'completed';
        if (isLocked) {
          // Locked rows are the ones with an outcome set
          let undoHandler: (() => void) | undefined;
          if (onUndo) {
            undoHandler = () => onUndo(row.event.id);
          }

          return (
            <LockedRow
              key={row.event.id}
              row={row}
              employeeName={getEmployeeNameForEvent(row.event)}
              onUndo={undoHandler}
              isUndoing={undoingEventId === row.event.id}
            />
          );
        }
        const isThisEditing = editingEventId === row.event.id;

        // Past rows can't have their slot/employee moved — hide Change.
        // Future needsOutcome rows remain editable.
        const editHandler = !row.isPast && onToggleEdit
          ? () => onToggleEdit(row.event.id)
          : undefined;
        const recordOutcomeHandler = onRecordOutcome
          ? () => onRecordOutcome(row.event.id)
          : undefined;

        return (
          <EventCard
            key={row.event.id}
            row={row}
            employeeName={getEmployeeNameForEvent(row.event)}
            onEdit={editHandler}
            onRecordOutcome={recordOutcomeHandler}
            isEditing={isThisEditing}
            inlineEditContent={isThisEditing ? inlineEditContent : null}
          />
        );
      })}

      {isOpen && onBookFollowUp && (
        <>
          <div
            className={`${styles.bookPlaceholder} ${isFollowupNeeded ? styles.bookPlaceholder_purple : ''}`}
            data-testid="series-book-followup"
            data-variant={isFollowupNeeded ? 'purple' : 'default'}
          >
            <div className={styles.bookPlaceholderBody}>
              <div className={styles.bookPlaceholderLabel}>{nextFollowUpLabel}</div>
              <div className={styles.bookPlaceholderSub}>
                {intl.formatMessage({ id: 'seriesbooking.placeholder.notYetBooked' })}
              </div>
            </div>
            <Button
              variant="outlined"
              size="small"
              onClick={onBookFollowUp}
              endIcon={<ArrowForwardIcon />}
              data-testid="series-book-button"
              className={styles.bookBtn}
            >
              {intl.formatMessage({ id: 'seriesbooking.common.book' })}
            </Button>
          </div>
          <div className={styles.bookHint}>
            {intl.formatMessage({ id: 'seriesbooking.placeholder.considerOutcomeFirst' })}
          </div>
        </>
      )}
    </div>
  );
}

SeriesEditTimelineComponent.defaultProps = {
  onUndo: undefined,
  undoingEventId: null,
};

export const SeriesEditTimeline = React.memo(SeriesEditTimelineComponent);
